import React, { useState, useEffect } from 'react';
import {dealCards, openCard, closeUnrevealed, hasMismatched, variantRevealed, numberOfVariants} from './models/GameModel';
import {nextPlayerTurn, increasePlayerScore, resetPlayers, createPlayers, setPlayerName, winnerName} from './models/PlayersModel';
import * as utils from './models/utils';
import Card from './components/Card';
import Board from './components/Board';
import Player from './components/Player';
import LeadersTable from './components/LeadersTable';
import WinnerBanner from './components/WinnerBanner';
import './App.css';

const NUMBER_OF_VARIANTS = 4;

function randomEmoji(n) {
  // UTF codepoints for emoji: 0x1F600 - 0x1F64F
  const emoji = [...Array(0x4F).keys()]
    .map(n => String.fromCodePoint(0x1F600+n));

  utils.shuffleArray(emoji);

  return emoji;
}

function Game() {
  const [cards, setCards] = useState(dealCards(NUMBER_OF_VARIANTS));
  const [emoji, setEmoji] = useState(randomEmoji(NUMBER_OF_VARIANTS));
  const [players, setPlayers] = useState(createPlayers());

  const [history, setHistory] = useState([]);

  useEffect(() => {
    setCards(JSON.parse(window.localStorage.getItem('cards')) || dealCards(NUMBER_OF_VARIANTS));
    setEmoji(JSON.parse(window.localStorage.getItem('emoji')) || randomEmoji(NUMBER_OF_VARIANTS));
    setPlayers(JSON.parse(window.localStorage.getItem('players')) || createPlayers());
  }, []);

  useEffect(() => {
    window.localStorage.setItem('cards', JSON.stringify(cards));
    window.localStorage.setItem('emoji', JSON.stringify(emoji));
    window.localStorage.setItem('players', JSON.stringify(players));
  }, [cards, players, emoji]);

  // i - index of a card has been clicked
  function handleCardClick(i) {
    if (cards[i].open) return; // do nothing if card is already opened

    // save current state
    const state = {
      cards: utils.deepCopy(cards),
      players: utils.deepCopy(players)
    };
    setHistory(history.concat(state));

    // move to next state
    let cardsNext = openCard(cards, i);
    setCards(cardsNext);

    if (hasMismatched(cardsNext)) { // if wrong card opened
      setPlayers(nextPlayerTurn(players)); // change player
      window.setTimeout(() => { // close wrongly opened cards
        setCards(closeUnrevealed(cards));
      }, 500);
    }

    if (variantRevealed(cardsNext, cardsNext[i].variant)) { // success! the variant is revealed
      setPlayers(increasePlayerScore(players)); // score the lucky fellow
    }
  }

  function resetGame() {
    setCards(dealCards(NUMBER_OF_VARIANTS));
    setEmoji(randomEmoji(NUMBER_OF_VARIANTS));
    setPlayers(resetPlayers(players));
    setHistory([]);
  }

  function undo() {
    if (history.length > 0) {
      setCards(history[history.length-1].cards);
      setPlayers(history[history.length-1].players);
      setHistory(history.slice(0, -1));
    }
  }

  function handleUserNameChange(e,i) {
    setPlayers(setPlayerName(players, i, e.target.value));
  }

  const playerElements = players.map((player, i) => 
    <Player active={player.hasTurn} 
            name={player.name} 
            score={player.score} 
            onChange={e => handleUserNameChange(e, i)}
            key={i}/>
  );

  const cardElements = cards.map((card, i) => 
    <Card cardIndex={i} 
          open={card.open} 
          onClick={handleCardClick}
          key={i}>
      <div>
        {emoji[card.variant]}
      </div>
    </Card>
  );

  return (
    <>
      <header>Turn over 3 cards. Find 3 matches - get a score!</header>
      <main>
        <div>
          <LeadersTable>{playerElements}</LeadersTable>
          <div className="button-bar">
            <button onClick={resetGame}>Restart</button>
            <button onClick={undo}>Undo</button>
          </div>
          <WinnerBanner name={winnerName(players, numberOfVariants(cards))} />
        </div>
        <div>
          <Board>{cardElements}</Board>
        </div>
      </main>
    </>
  );
}

export default Game;
